import { useState, useEffect } from 'react';
import { useAuthState } from '../hooks/useAuthState';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { sendFriendRequest } from '../lib/firestore/friends';
import '../styles/UserProfileModal.css';

const UserProfileModal = ({ isOpen, onClose, userId }) => {
  const { user } = useAuthState();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFriend, setIsFriend] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && userId && user) {
      loadUserProfile();
    } else {
      setUserData(null);
      setLoading(true);
      setIsFriend(false);
      setHasPendingRequest(false);
      setError('');
    }
  }, [isOpen, userId, user]);

  const loadUserProfile = async () => {
    if (!userId || !user) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Load user data
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        setError('User not found');
        setLoading(false);
        return;
      }

      const data = userDoc.data();
      setUserData({
        id: userId,
        ...data,
      });

      // Check if already friends
      const pairId = [user.uid, userId].sort().join('_');
      const friendshipRef = doc(db, 'friendships', pairId);
      const friendship = await getDoc(friendshipRef);
      setIsFriend(friendship.exists());

      // Check if friend request already exists
      if (!friendship.exists()) {
        const requestsRef = collection(db, 'friendRequests');
        const existingQ = query(
          requestsRef,
          where('fromUserId', '==', user.uid),
          where('toUserId', '==', userId),
          where('status', 'in', ['pending', 'accepted'])
        );
        const existing = await getDocs(existingQ);
        setHasPendingRequest(!existing.empty);
      }
    } catch (err) {
      console.error('Error loading user profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBeFriends = async () => {
    if (!user || !userId || sendingRequest || isFriend || hasPendingRequest) return;

    setSendingRequest(true);
    setError('');

    try {
      await sendFriendRequest(user.uid, userId);
      setHasPendingRequest(true);
    } catch (err) {
      console.error('Error sending friend request:', err);
      setError(err.message || 'Failed to send friend request');
    } finally {
      setSendingRequest(false);
    }
  };

  if (!isOpen) return null;

  const displayName = userData?.firstName && userData?.lastName
    ? `${userData.firstName} ${userData.lastName}`.trim()
    : userData?.firstName || userData?.lastName || 'User';
  
  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="user-profile-modal-overlay" onClick={onClose}>
      <div className="user-profile-modal-card" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="user-profile-loading">Loading...</div>
        ) : error ? (
          <div className="user-profile-error">
            <p>{error}</p>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        ) : userData ? (
          <>
            <div className="user-profile-image-wrapper">
              {userData.profilePicture ? (
                <img 
                  src={userData.profilePicture} 
                  alt={displayName}
                  className="user-profile-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="user-profile-avatar">
                  {initials || '👤'}
                </div>
              )}
            </div>
            <h2 className="user-profile-name">{displayName}</h2>
            {error && <p className="user-profile-error-text">{error}</p>}
            <div className="user-profile-actions">
              {isFriend ? (
                <button className="btn btn-secondary" disabled>
                  ✓ Friends
                </button>
              ) : hasPendingRequest ? (
                <button className="btn btn-secondary" disabled>
                  Friend Request Sent
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={handleBeFriends}
                  disabled={sendingRequest || user?.uid === userId}
                >
                  {sendingRequest ? 'Sending...' : 'Be Friends'}
                </button>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default UserProfileModal;


